import { useEffect, useRef } from "react";
import { Chips } from "primereact/chips";
import { Icon } from "../Icon";
import { v4 as uuidv4 } from "uuid";

import styles from "./MultiInput.module.css";
import inputStyles from "../Inputs/Input.module.css";

export interface MultiCodeInputProps {
  label?: string;
  placeholder?: string;
  values?: string[];
  setValues: (values: string[]) => void;
  validationError?: string;
  className?: string;
  wrapperStyles?: React.CSSProperties;
  allowDuplicate?: boolean;
  maxValues?: number;
  required?: boolean;
  description?: string;
}

export default function MultiStringInput({
  label,
  placeholder,
  values = [],
  setValues,
  validationError,
  className,
  allowDuplicate = false,
  maxValues,
  required,
  description,
}: Readonly<MultiCodeInputProps>) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputId = uuidv4();
  const labelId = label
    ? `${label.replace(/\s+/g, "-").toLowerCase()}-label`
    : "chips-label";
  const descriptionId = description ? `${inputId}-description` : undefined;
  const errorId = validationError ? `${inputId}-error` : undefined;
  const listId = `${inputId}-list`;

  const getInput = () => {
    const elem = document.querySelector(".p-chips-input-token");
    const input = elem?.getElementsByTagName("input");

    return input ? input[0] : undefined;
  };

  useEffect(() => {
    if (!wrapperRef.current) return;

    const chipsContainer = wrapperRef.current.querySelector(".p-chips");
    if (!chipsContainer) return;

    const ulElement = chipsContainer.querySelector("ul");
    if (!ulElement) return;

    const setAccessibilityAttributes = () => {
      ulElement.removeAttribute("role");
      ulElement.removeAttribute("aria-orientation");

      ulElement.setAttribute("role", "list");
      ulElement.id = listId;

      if (label) {
        ulElement.setAttribute("aria-labelledby", labelId);
        ulElement.removeAttribute("aria-label");
      } else {
        ulElement.setAttribute("aria-label", "Selected items");
        ulElement.removeAttribute("aria-labelledby");
      }

      const listItems = Array.from(ulElement.children).filter(
        (child) => child.tagName.toLowerCase() === "li"
      ) as HTMLLIElement[];

      listItems.forEach((li) => {
        li.removeAttribute("role");
        li.removeAttribute("aria-selected");
        li.setAttribute("role", "listitem");
      });
    };

    const setInputAccessibilityAttributes = () => {
      const input = getInput();
      if (input) {
        input.id = inputId;
        if (label) {
          input.setAttribute("aria-labelledby", labelId);
        } else {
          input.setAttribute("aria-label", "Multi-string input");
        }

        const describedBy = [descriptionId, errorId].filter(Boolean).join(" ");
        if (describedBy) {
          input.setAttribute("aria-describedby", describedBy);
        } else {
          input.removeAttribute("aria-describedby");
        }

        if (required) {
          input.setAttribute("aria-required", "true");
        } else {
          input.removeAttribute("aria-required");
        }
      }
    };

    const timeoutId = setTimeout(() => {
      setAccessibilityAttributes();
      setInputAccessibilityAttributes();
    }, 0);

    const observer = new MutationObserver(() => {
      setAccessibilityAttributes();
      setInputAccessibilityAttributes();
    });

    observer.observe(ulElement, {
      childList: true,
      subtree: false,
    });

    observer.observe(chipsContainer, {
      childList: true,
      subtree: true,
    });

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [
    label,
    labelId,
    values,
    inputId,
    listId,
    descriptionId,
    errorId,
    required,
  ]);

  function onDelete(item: string) {
    const newItems = [...values];
    const itemIndex = newItems.findIndex((i) => i === item);
    newItems.splice(itemIndex, 1);
    setValues(newItems);
  }

  function Item(item: string) {
    return (
      <div className={styles.item}>
        <span className={styles.name}>{item}</span>
        <button
          type="button"
          className={styles.deleteIcon}
          onClick={(e) => {
            e.preventDefault();
            onDelete(item);
          }}
          aria-label={`Remove ${item}`}
        >
          <Icon
            name="x"
            color="Grey-400"
            size={18}
            hoverEffect
            ariaLabel={`Remove ${item}`}
          />
        </button>
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className={styles.multiStringWrapper}>
      {label && (
        <label id={labelId} htmlFor={inputId} className="strong">
          {required && <span className={inputStyles.requiredIcon}>*</span>}
          {label}
        </label>
      )}
      {!label && required && (
        <span className={inputStyles.requiredIcon} aria-hidden="true">
          *
        </span>
      )}
      <Chips
        inputId={inputId}
        placeholder={values.length === 0 ? placeholder : undefined}
        value={values.length === 0 ? undefined : values}
        onChange={(e) => {
          const valuesToSet =
            e.value?.filter((value) => value.length && !value.includes(",")) ||
            [];
          setValues(maxValues ? valuesToSet.splice(0, maxValues) : valuesToSet);
        }}
        onBlur={() => {
          // Clear input on blur by simulate enter press
          const input = getInput();

          if (input) {
            const event = new KeyboardEvent("keydown", {
              keyCode: 13,
              bubbles: true,
            });
            input.dispatchEvent(event);
          }
        }}
        itemTemplate={Item}
        allowDuplicate={allowDuplicate}
        separator=","
        className={className ?? ""}
        removable={false}
      />
      {description && (
        <p id={descriptionId} className="description">
          {description}
        </p>
      )}
      {validationError && (
        <div id={errorId} className={styles.validationError} role="alert">
          {validationError}
        </div>
      )}
    </div>
  );
}
